import { useState, useRef, useEffect } from "react";
import { SiteVideoData, SiteImageData } from "../../../../interfaces";
import { MediaContentTypes } from "../../../../../constants/contentTypes";
import { VerticalVideoPlayer } from "../../components/VerticalVideoPlayer";
import { VerticalShareArea } from "../../components/VerticalShareArea";
import { SpeedMenuButton } from "../../components/SpeedMenuButton";
import { DesignItemProps } from "../../interfaces";
import { getCachedRylzTalkback, loadRylzTalkback } from "./rylzTalkbackCache";
require("./m_ynet_site_rylzMediaItem.less");

export const RylzMediaItem = (props: DesignItemProps) => {
    const { videoItem, index, domain, isMobile, languageCode, showPrerollAds, isTitleVisible, isSubTitleVisible, isGalleryView, shouldDisplayArrow, currentPlayingVideo,
        containerStyle, dynamicHeight, dynamicWidth, onNextSlide, onVideoOverlayClose } = props;
    const [isSubtitleOpen, setIsSubtitleOpen] = useState(false);
    const [isClamped, setIsClamped] = useState(false);
    const [isPortrait, setIsPortrait] = useState(true);
    const [playerApiRef, setPlayerApiRef] = useState(null) as any;
    const cachedTalkback = videoItem && (videoItem as any).articleId
        ? getCachedRylzTalkback(String((videoItem as any).articleId))
        : undefined;
    const [talkbackCount, setTalkbackCount] = useState<number | undefined>(cachedTalkback && cachedTalkback.count);
    const [videoDuration, setVideoDuration] = useState<number>(0);
    const [isSeeking, setIsSeeking] = useState(false);
    const isSeekingRef: any = useRef(false);
    const wasPlayingRef: any = useRef(false);
    const subtitleRef = useRef(null) as any;

    const item = videoItem as any;
    const { itemId, media, title, subTitle, titleLink } = videoItem;

    // Rylz-specific fields (passed as extra properties on the SiteItemData)
    const date: string = item.date || "";
    const author: string = item.author || "";
    const labelUrl: string = item.labelUrl || "";
    const labelName: string = item.labelName || "";
    const readMoreTitle: string = item.readMoreTitle || "";
    const labelAuthor: string = item.labelAuthor || "";
    const articleId: string | undefined = item.articleId || undefined;
    const publishedLink: string = item.publishedLink || titleLink || "";

    // Media type detection
    const isVideo = media && (media as any).mediaType === MediaContentTypes.MEDIA_VIDEO;
    const isImage = media && (media as any).mediaType === MediaContentTypes.MEDIA_IMAGE;
    const imageUrl = isImage ? (media as SiteImageData).url : "";
    const imageAlt = isImage ? ((media as SiteImageData).alt || title || "") : "";
    const videoPageId = isVideo ? (media as SiteVideoData).videoPageId : "";

    // Process video URL and extract DRM / publishedLink query params
    const { processedMedia, videoPublishedLink } = isVideo && media ? (() => {
        const videoData = media as SiteVideoData;
        const url = videoData.url || "";

        // Extract URL before "?"
        const urlWithoutQuery = url.split("?")[0];

        // Parse query string to extract drm and publishedLink parameters
        const urlObj = new URL(url, "http://example.com");
        const drmParam = urlObj.searchParams.get("drm");
        const isDrm = drmParam === "1";
        const publishedLinkParam = urlObj.searchParams.get("publishedLink") || "";

        return {
            processedMedia: {
                ...videoData,
                url: urlWithoutQuery,
                is_drm: isDrm
            },
            videoPublishedLink: publishedLinkParam
        };
    })() : { processedMedia: media, videoPublishedLink: "" };

    const shareLink = (isVideo && videoPublishedLink) || publishedLink;

    // const showSkipButtons = isVideo && videoDuration > 60; // disabled for now belongs to video forward backwards buttons

    // Aspect ratio detection
    const PORTRAIT_RATIO_THRESHOLD = 0.75;
    const videoAspectRatio = isVideo ? (media as SiteVideoData).aspectRatio : undefined;

    // Localization
    const lang = languageCode || "";
    const readMoreLabel = lang.includes("en") ? "Read more..." : lang.includes("ru") ? "Читать далее..." : "קראו עוד...";
    const closeLabel = lang.includes("en") ? "Close" : lang.includes("ru") ? "Закрыть" : "צמצום";
    const nextPageLabel = lang.includes("en") ? "Scroll for the next rylz" : lang.includes("ru") ? "прокрутите для следующей рилз" : "ניתן לדפדף לריל הבא";
    const copyLinkLabel = lang.includes("en") ? "Copy link" : lang.includes("ru") ? "Скопировать ссылку" : "העתק קישור";
    const linkCopiedLabel = lang.includes("en") ? "Link copied to clipboard" : lang.includes("ru") ? "Ссылка скопирована" : "הקישור הועתק ללוח";

    const subtitleText = item.text || subTitle || "";
    const currentSlideIndex = currentPlayingVideo;
    const isNearbySlide = currentSlideIndex === undefined || Math.abs(index - currentSlideIndex) <= 1;
    const subtitleClass = `subtitle ${isSubtitleOpen ? "" : "close"}`;
    const itemType = isVideo ? "video-item" : isImage ? "image-item" : "";
    const aspectClass = isPortrait ? "portrait-media" : "landscape-media";
    const itemClass = `rylz-media-item ${itemType} ${isMobile ? isSubtitleOpen ? "openMode" : "standardMode" : ""} ${isSeeking ? "is-seeking" : ""}`;
    const shareItemType = isImage ? "rylz_image" : isVideo ? "rylz_video" : "rylz_item";

    useEffect(() => {
        if (isVideo && videoAspectRatio) {
            const [w, h] = videoAspectRatio.split(":").map(Number);
            setIsPortrait(w / h <= PORTRAIT_RATIO_THRESHOLD);
        }
    }, [isVideo, videoAspectRatio]);

    useEffect(() => {
        const el = subtitleRef.current;
        if (el) {
            setIsClamped(el.scrollHeight > el.clientHeight);
        }
    }, [subTitle, item.text]);

    useEffect(() => {
        if (!articleId) return;
        let cancelled = false;
        loadRylzTalkback(articleId).then((info) => {
            if (cancelled || info.blocked) return;
            setTalkbackCount(info.count);
        });
        return () => { cancelled = true; };
    }, [articleId]);

    useEffect(() => {
        if (!isVideo || !media) return;
        const duration = (media as SiteVideoData).duration;
        if (duration) {
            setVideoDuration(duration);
        }
    }, [isVideo, media]);

    useEffect(() => {
        if (!isVideo) return;

        const container = document.getElementById(`VerticalVideoItem_rylzMediaItem_${itemId}`);
        if (!container) return;

        let seekTimeout: number | undefined;
        let isMounted = true;

        const getVideoElement = (): HTMLVideoElement | null => {
            const root = document.getElementById(`VerticalVideoItem_rylzMediaItem_${itemId}`);
            if (!root) return null;
            const engine = root.querySelector('#player .fp-engine') as HTMLVideoElement;
            return engine && engine.tagName === 'VIDEO' ? engine : null;
        };

        const preventScroll = (e: TouchEvent) => {
            if (!isSeekingRef.current) return;
            if (playerDetails && playerDetails.contains(e.target as Node)) return;
            e.preventDefault();
            e.stopPropagation();
        };

        const onPlayerDetailsTouchMove = (e: TouchEvent) => {
            if (!isSeekingRef.current) return;
            e.stopPropagation();
        };

        const onSeekStart = () => {
            if (!isMounted || isSeekingRef.current) return;
            isSeekingRef.current = true;
            if (seekTimeout) { clearTimeout(seekTimeout); seekTimeout = undefined; }

            const videoEl = getVideoElement();
            if (videoEl) {
                wasPlayingRef.current = !videoEl.paused;
                if (!videoEl.paused) videoEl.pause();
            }

            setIsSeeking(true);
        };

        const onSeekEnd = () => {
            if (!isMounted || !isSeekingRef.current) return;
            isSeekingRef.current = false;
            if (seekTimeout) clearTimeout(seekTimeout);

            if (wasPlayingRef.current) {
                const videoEl = getVideoElement();
                if (videoEl) {
                    const p: any = videoEl.play();
                    if (p && typeof p.catch === 'function') p.catch(() => { });
                }
                wasPlayingRef.current = false;
            }

            seekTimeout = window.setTimeout(() => {
                if (isMounted) setIsSeeking(false);
            }, 800);
        };

        const onPlayerDetailsTouchStart = (e: TouchEvent) => {
            e.stopPropagation();
            onSeekStart();
        };

        const onPlayerDetailsMouseDown = (e: MouseEvent) => {
            e.stopPropagation();
            onSeekStart();
        };

        const overlayPlayer = container.closest('.vertical-video-overlay-player');
        const slickList = overlayPlayer ? overlayPlayer.querySelector('.slick-list') as HTMLElement | null : null;
        const playerDetails = container.querySelector('.player-details') as HTMLElement | null;

        if (playerDetails) {
            playerDetails.addEventListener('touchstart', onPlayerDetailsTouchStart, { passive: true });
            playerDetails.addEventListener('touchmove', onPlayerDetailsTouchMove, { passive: true });
            playerDetails.addEventListener('mousedown', onPlayerDetailsMouseDown);
        }
        if (slickList) {
            slickList.addEventListener('touchmove', preventScroll, { passive: false });
        }
        document.addEventListener('touchend', onSeekEnd);
        document.addEventListener('touchcancel', onSeekEnd);
        document.addEventListener('mouseup', onSeekEnd);

        return () => {
            isMounted = false;
            if (seekTimeout) clearTimeout(seekTimeout);
            if (playerDetails) {
                playerDetails.removeEventListener('touchstart', onPlayerDetailsTouchStart);
                playerDetails.removeEventListener('touchmove', onPlayerDetailsTouchMove);
                playerDetails.removeEventListener('mousedown', onPlayerDetailsMouseDown);
            }
            if (slickList) {
                slickList.removeEventListener('touchmove', preventScroll);
            }
            document.removeEventListener('touchend', onSeekEnd);
            document.removeEventListener('touchcancel', onSeekEnd);
            document.removeEventListener('mouseup', onSeekEnd);
        };
    }, [isVideo, itemId]);

    // Rylz-only: strip Flowplayer scrubber title tooltip; time is shown via seek-time-display
    useEffect(() => {
        if (!isVideo) return;

        const container = document.getElementById(`VerticalVideoItem_rylzMediaItem_${itemId}`);
        if (!container) return;

        const clearTimelineTitle = () => {
            const timeline = container.querySelector(".timeline .fp-timeline") as HTMLElement | null;
            if (timeline && timeline.hasAttribute("title")) {
                timeline.removeAttribute("title");
            }
        };

        clearTimelineTitle();
        const observer = new MutationObserver(clearTimelineTitle);
        observer.observe(container, { childList: true, subtree: true, attributes: true, attributeFilter: ["title"] });

        return () => observer.disconnect();
    }, [isVideo, itemId]);

    const onImageLoad = (e: any) => {
        const img = e.target;
        const ratio = img.naturalWidth / img.naturalHeight;
        setIsPortrait(ratio <= PORTRAIT_RATIO_THRESHOLD);
    };

    const skipVideo = (seconds: number) => {
        const player = playerApiRef && playerApiRef.current;
        if (!player) return;
        const videoEl = player.video || player;
        if (videoEl && typeof videoEl.currentTime === "number") {
            videoEl.currentTime = Math.max(0, videoEl.currentTime + seconds);
        }
    };

    const formatCount = (count: number | undefined): string => {
        if (count === undefined || count === null) return "";
        if (count >= 1000) return `${Math.round(count / 1000)}K`;
        return count.toString();
    };

    const renderLabel = () => {
        if (labelAuthor) {
            return (
                <div className="rylz-label commentary-label">
                    {labelUrl &&
                        <span className="author-image-wrapper">
                            <img className="author-image" src={labelUrl} alt={labelAuthor} />
                        </span>
                    }
                    <span className="label-text">
                        <div className="title" dangerouslySetInnerHTML={{ __html: labelName || "פרשנות" }}></div>
                        <div className="author" dangerouslySetInnerHTML={{ __html: labelAuthor }}></div>
                    </span>
                </div>
            );
        }

        if (labelUrl) {
            return (
                <div className="rylz-label logo-label">
                    <img className="logo-image" src={labelUrl} alt={labelName} />
                </div>
            );
        }

        if (labelName) {
            return (
                <div className="rylz-label text-label">
                    <span className="label-text" dangerouslySetInnerHTML={{ __html: labelName }} {...(isSubTitleVisible && subtitleText ? { onClick: () => setIsSubtitleOpen(!isSubtitleOpen) } : {})}></span>
                </div>
            );
        }

        return null;
    };

    return (
        <div className={itemClass} id={`VerticalVideoItem_rylzMediaItem_${itemId}`} style={containerStyle}>
            <div className={`media-wrapper ${aspectClass}`}>
                {isVideo && isNearbySlide &&
                    <VerticalVideoPlayer
                        index={index}
                        isRylz={true}
                        isMobile={isMobile}
                        onNextSlide={onNextSlide}
                        dynamicWidth={dynamicWidth}
                        dynamicHeight={dynamicHeight}
                        isGalleryView={isGalleryView}
                        showPrerollAds={showPrerollAds}
                        itemId={`rylzMediaItem_${itemId}`}
                        prerollIndex={videoItem.prerollIndex}
                        media={processedMedia as SiteVideoData}
                        hidePrerollAds={videoItem.hidePrerollAds}
                        currentPlayingVideo={currentPlayingVideo}
                        onPlayerApiReady={(ref) => setPlayerApiRef(ref)}
                    />
                }

                {isImage && isNearbySlide && publishedLink
                    ? <a href={publishedLink}><img className="rylz-image" src={imageUrl} alt={imageAlt} onLoad={onImageLoad} /></a>
                    : isImage && isNearbySlide && <img className="rylz-image" src={imageUrl} alt={imageAlt} onLoad={onImageLoad} />
                }
            </div>

            <div className="main-ynet-logo"></div>

            <div className="content-div">
                <div className="item-info">
                    <div className="text-content">
                        {renderLabel()}

                        {isTitleVisible && <div className="title" dangerouslySetInnerHTML={{ __html: title || "" }} {...(isSubTitleVisible && subtitleText ? { onClick: () => setIsSubtitleOpen(!isSubtitleOpen) } : {})} />}

                        {isSubTitleVisible && subtitleText &&
                            <div className={subtitleClass} ref={subtitleRef} onClick={() => setIsSubtitleOpen(!isSubtitleOpen)}>
                                <span dangerouslySetInnerHTML={{ __html: subtitleText }} />

                                {isClamped &&
                                    <span className={`read-more ${isSubtitleOpen ? "close-label" : ""}`}>
                                        {isSubtitleOpen ? closeLabel : readMoreLabel}
                                    </span>
                                }
                            </div>
                        }

                        {(author || date) &&
                            <div className="author-date">
                                {author && !labelAuthor && <span className="author" dangerouslySetInnerHTML={{ __html: author }} />}
                                {author && !labelAuthor && date && <span className="separator"> | </span>}
                                {date && <span className="date">{date}</span>}
                            </div>
                        }

                        {publishedLink && readMoreTitle &&
                            <a className="full-article-link" href={publishedLink}>
                                <span className="link-text" dangerouslySetInnerHTML={{ __html: readMoreTitle }}></span>
                                <span className="link-arrow"></span>
                            </a>
                        }

                        {shouldDisplayArrow && isNearbySlide &&
                            <div className="arrow-next">
                                <video src="https://www.ynet.co.il/images/picsrv/two-arrows-animation.webm" className="arrow-gif" autoPlay loop muted playsInline></video>
                                <div className="arrow-text">{nextPageLabel}</div>
                            </div>
                        }
                    </div>

                    <div className="btns-content">
                        {/* {showSkipButtons &&
                            <div className="skip-forward-btn skip-icon" onClick={() => skipVideo(10)}></div>
                        }

                        {showSkipButtons &&
                            <div className="skip-backward-btn skip-icon" onClick={() => skipVideo(-10)}></div>
                        } */}

                        {isVideo && playerApiRef &&
                            <SpeedMenuButton cycleOnTap playerApiRef={playerApiRef} speedOptions={[1, 1.5, 2]} />
                        }

                        {isVideo && <div className="mute-btn" id="volume-target"></div>}

                        {articleId &&
                            <a className="talkback-btn" href={publishedLink ? `${publishedLink}#commentsSection` : undefined}>
                                <span className="talkback-icon"></span>
                                {talkbackCount !== undefined && talkbackCount > 0 && <span className="talkback-count">{formatCount(talkbackCount)}</span>}
                            </a>
                        }

                        {shareLink &&
                            <VerticalShareArea
                                domain={domain}
                                link={shareLink}
                                isMobile={isMobile}
                                title={title || ""}
                                copyLinkLabel={copyLinkLabel}
                                videoPageId={videoPageId || ""}
                                articleId={articleId || itemId}
                                linkCopiedLabel={linkCopiedLabel}
                                analyticShareLabel={shareItemType}
                                shareItemType={videoPageId ? "verticalVideo" : "rylz"}
                            />
                        }
                    </div>
                </div>

                {isVideo && (
                    <div className="player-details">
                        <div className="seek-time-display">
                            <span className="elapsed" id="elapsed-target"></span>
                            <span className="separator">/</span>
                            <span className="duration" id="duration-target"></span>
                        </div>
                        <div className="timeline" id="timeline-target"></div>
                    </div>
                )}
            </div>
        </div>
    );
};
